import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import AdminLayout from '@/components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap, Plus, Trash2, ChevronUp, ChevronDown, Loader2, Save, Image as ImageIcon, Upload, Search, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';
const API = `${BACKEND_URL}/api`;

export const AdminOffers = () => {
  const { user, getAuthHeaders, fetchSettings: refreshAppSettings } = useApp();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [promoBanners, setPromoBanners] = useState([]);
  const [savingPromo, setSavingPromo] = useState(false);
  const [uploadingPromoIdx, setUploadingPromoIdx] = useState(null);

  // Offer Products State
  const [offerProducts, setOfferProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const [addingOfferId, setAddingOfferId] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/login'); return; }
    fetchSettings();
    fetchOfferProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings/admin`, { headers: getAuthHeaders() });
      setSettings(res.data);
      // Fetch promo banners from dedicated endpoint
      try {
        const pbRes = await axios.get(`${API}/promo-banners`);
        setPromoBanners(pbRes.data.banners || []);
      } catch (e) { console.warn('Could not fetch promo banners'); }
    } catch (e) { toast.error('Failed to load settings'); }
    setLoading(false);
  };

  const fetchOfferProducts = async () => {
    setLoadingProducts(true);
    try {
      // Raised limit to 500 — silently capping at 100 would miss items
      const res = await axios.get(`${API}/products`, {
        params: { is_weekly_offer: true, limit: 500 },
      });
      setOfferProducts(res.data.products || []);
    } catch (e) {
      toast.error('Failed to load offer products');
    }
    setLoadingProducts(false);
  };

  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }
      setSearching(true);
      try {
        // Use axios params so special chars (&, #, %) are URL-encoded automatically
        const res = await axios.get(`${API}/products`, {
          params: { search: searchQuery.trim(), limit: 10 },
        });
        setSearchResults(res.data.products || []);
      } catch (e) {
        // ignore — user will retry or refine query
      }
      setSearching(false);
    }, 400);
    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const handleSaveSettings = async (updatedSettings) => {
    try {
      await axios.put(`${API}/settings`, updatedSettings, { headers: getAuthHeaders() });
      toast.success('Settings saved successfully');
      await refreshAppSettings();
    } catch (e) { toast.error('Failed to save settings'); }
  };

  const updateSetting = (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    handleSaveSettings(updated);
  };

  const handleAddOffer = async (product) => {
    setAddingOfferId(product.id);
    try {
      await axios.put(`${API}/products/${product.id}`, {
        is_weekly_offer: true,
        offer_price: null
      }, { headers: getAuthHeaders() });
      toast.success(`"${product.name}" added to offers!`);
      setSearchQuery('');
      setSearchResults([]);
      fetchOfferProducts();
    } catch (e) {
      toast.error('Failed to add offer: ' + (e.response?.data?.detail || e.message));
    }
    setAddingOfferId(null);
  };

  const handleUpdateTag = async (product, newTag) => {
    try {
      const updatedSpecs = { ...(product.specifications || {}) };
      if (newTag.trim() !== '') {
        updatedSpecs['_offer_tag'] = newTag.trim();
      } else {
        delete updatedSpecs['_offer_tag'];
      }
      await axios.put(`${API}/products/${product.id}`, {
        is_weekly_offer: true,
        specifications: updatedSpecs
      }, { headers: getAuthHeaders() });
      toast.success('Offer tag updated');
      fetchOfferProducts();
    } catch (e) {
      toast.error('Failed to update tag');
    }
  };

  const handleRemoveOffer = async (product) => {
    try {
      await axios.put(`${API}/products/${product.id}`, { 
        is_weekly_offer: false, 
        offer_price: null 
      }, { headers: getAuthHeaders() });
      
      toast.success('Removed from offers');
      fetchOfferProducts();
    } catch (e) {
      toast.error('Failed to remove offer');
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProducts(offerProducts.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (checked, id) => {
    if (checked) {
      setSelectedProducts(prev => [...prev, id]);
    } else {
      setSelectedProducts(prev => prev.filter(pId => pId !== id));
    }
  };

  const handleBulkRemove = async () => {
    if (!selectedProducts.length) return;
    setBulkRemoving(true);
    try {
      // Batch in groups of 10 — avoids overwhelming the server with N parallel requests
      const BATCH_SIZE = 10;
      for (let i = 0; i < selectedProducts.length; i += BATCH_SIZE) {
        const batch = selectedProducts.slice(i, i + BATCH_SIZE);
        await Promise.all(
          batch.map(id =>
            axios.put(
              `${API}/products/${id}`,
              { is_weekly_offer: false, offer_price: null },
              { headers: getAuthHeaders() }
            )
          )
        );
      }
      toast.success(`Removed ${selectedProducts.length} products from offers`);
      setSelectedProducts([]);
      fetchOfferProducts();
    } catch (e) {
      toast.error('Failed to remove some products');
      fetchOfferProducts();
    }
    setBulkRemoving(false);
  };

  if (loading) return <AdminLayout title="Offers" subtitle="Manage store offers and banners"><div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout title="Offers" subtitle="Manage store offers and banners">

      {/* Special Offer Banner Settings */}
      <Card className="border-2 border-amber-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Special Offer Banner
          </CardTitle>
          <p className="text-sm text-muted-foreground">Manage the special offer banner displayed on the homepage.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
            <div>
              <Label className="text-base font-medium">Show Special Offer Banner</Label>
              <p className="text-sm text-muted-foreground">Toggle visibility on the homepage</p>
            </div>
            <Switch 
              checked={settings.special_offer_enabled !== false} 
              onCheckedChange={(v) => updateSetting('special_offer_enabled', v)} 
            />
          </div>
          <div className="space-y-2">
            <Label>Offer Title</Label>
            <div className="flex gap-2">
              <Input 
                value={settings.special_offer_title || ''} 
                placeholder="e.g. Special Weekly Offers"
                onChange={(e) => setSettings({ ...settings, special_offer_title: e.target.value })} 
              />
              <Button onClick={() => updateSetting('special_offer_title', settings.special_offer_title)} variant="outline">Save</Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Offer Description</Label>
            <div className="flex gap-2">
              <Input 
                value={settings.special_offer_desc || ''} 
                placeholder="e.g. Get up to 30% off on premium sanitaryware & electrical tools"
                onChange={(e) => setSettings({ ...settings, special_offer_desc: e.target.value })} 
              />
              <Button onClick={() => updateSetting('special_offer_desc', settings.special_offer_desc)} variant="outline">Save</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Offer Products Management */}
      <Card className="mt-6 border-2 border-amber-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-500" />
            Special Offer Products
            <Badge variant="secondary" className="ml-2">{offerProducts.length} items</Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground">Select the products that will be listed when a user clicks 'Explore Offers' on the homepage.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products to add to offers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
            {searchQuery && (
              <button 
                className="absolute right-3 top-2.5 text-muted-foreground hover:text-black"
                onClick={() => { setSearchQuery(''); setSearchResults([]); }}
              >
                <X className="h-4 w-4" />
              </button>
            )}

            {searchQuery.length >= 2 && (
              <div className="absolute top-12 left-0 right-0 bg-white border shadow-lg rounded-lg z-50 max-h-64 overflow-y-auto">
                {searching ? (
                  <div className="p-4 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2"/> Searching...</div>
                ) : searchResults.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">No products found.</div>
                ) : (
                  searchResults.map(p => (
                    <div key={p.id} className="border-b">
                      <div className="flex items-center justify-between p-3 hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                            {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover"/> : <ImageIcon className="w-full h-full p-2 text-gray-300"/>}
                          </div>
                          <div>
                            <p className="font-medium text-sm line-clamp-1">{p.name}</p>
                            <p className="text-xs text-muted-foreground">{p.sku}</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant={p.is_weekly_offer ? "secondary" : "default"}
                          disabled={p.is_weekly_offer || addingOfferId === p.id}
                          onClick={() => handleAddOffer(p)}
                          className={!p.is_weekly_offer ? "bg-[#0c1b44] text-white hover:bg-[#1a2d64]" : ""}
                        >
                          {addingOfferId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : p.is_weekly_offer ? 'Already Added' : 'Add to Offers'}
                        </Button>
                      </div>

                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="flex items-center justify-between mt-4 mb-2">
            {selectedProducts.length > 0 ? (
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={handleBulkRemove}
                disabled={bulkRemoving}
              >
                {bulkRemoving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Remove {selectedProducts.length} Selected
              </Button>
            ) : (
              <div />
            )}
          </div>

          <div className="rounded-md border">
            {loadingProducts ? (
              <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
            ) : offerProducts.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No products currently on offer. Search above to add some!</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="h-10 px-4 w-12 text-center">
                      <Checkbox 
                        checked={offerProducts.length > 0 && selectedProducts.length === offerProducts.length}
                        onCheckedChange={handleSelectAll}
                      />
                    </th>
                    <th className="h-10 px-4 text-left font-medium">Product</th>
                    <th className="h-10 px-4 text-left font-medium">Price</th>
                    <th className="h-10 px-4 text-left font-medium">Offer Tag</th>
                    <th className="h-10 px-4 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {offerProducts.map(product => (
                    <tr key={product.id} className="border-b">
                      <td className="p-4 text-center">
                        <Checkbox 
                          checked={selectedProducts.includes(product.id)}
                          onCheckedChange={(checked) => handleSelectProduct(checked, product.id)}
                        />
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                            {product.images?.[0] ? <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover"/> : <ImageIcon className="w-full h-full p-3 text-gray-300"/>}
                          </div>
                          <div>
                            <div className="font-medium line-clamp-1">{product.name}</div>
                            <div className="text-xs text-muted-foreground">{product.sku}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4 font-medium whitespace-nowrap">
                        <span>AED {product.price}</span>
                      </td>
                      <td className="p-4">
                        <Input 
                          placeholder="e.g. 10% OFF" 
                          defaultValue={product.specifications?._offer_tag || ''} 
                          onBlur={(e) => {
                            if (e.target.value !== (product.specifications?._offer_tag || '')) {
                              handleUpdateTag(product, e.target.value);
                            }
                          }}
                          className="w-32 h-8 text-sm"
                        />
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex items-center justify-end gap-2">

                          <Button variant="ghost" size="sm" className="text-red-500 hover:bg-red-50 hover:text-red-700" onClick={() => handleRemoveOffer(product)}>
                            <Trash2 className="h-4 w-4 mr-1" /> Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          
        </CardContent>
      </Card>

      {/* Promo Banners Management */}
      <Card className="mt-6 border-2 border-navy/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-[#0c1b44]" />
            Promo Banners
            <Badge variant="outline" className="ml-2">{promoBanners.length} banners</Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground">Manage the category highlight cards shown below the homepage slider. You can add, remove, reorder, and upload images for each banner.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {promoBanners.map((banner, idx) => (
            <Card key={idx} className="bg-gray-50 border">
              <CardContent className="p-4">
                <div className="flex flex-col lg:flex-row items-start gap-4">
                  {/* Image preview + upload */}
                  <div className="w-full lg:w-48 h-32 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0 relative group">
                    {banner.image ? (
                      <img
                        src={banner.image.startsWith('/api') ? `${window.location.origin}${banner.image}` : banner.image}
                        alt={banner.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground"><ImageIcon className="h-8 w-8" /></div>
                    )}
                    <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer">
                      {uploadingPromoIdx === idx
                        ? <Loader2 className="h-5 w-5 text-white animate-spin" />
                        : <Upload className="h-6 w-6 text-white" />}
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingPromoIdx(idx);
                        try {
                          const fd = new FormData(); fd.append('file', file);
                          const res = await axios.post(`${API}/upload/image`, fd, { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } });
                          const updated = [...promoBanners];
                          updated[idx] = { ...updated[idx], image: res.data.url };
                          setPromoBanners(updated);
                          toast.success('Image uploaded');
                        } catch (err) { toast.error('Upload failed'); }
                        setUploadingPromoIdx(null);
                        e.target.value = '';
                      }} />
                    </label>
                  </div>

                  {/* Fields */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 w-full">
                    <div className="space-y-1">
                      <Label className="text-xs">Badge Text</Label>
                      <Input
                        placeholder="e.g. PROFESSIONAL"
                        value={banner.badge || ''}
                        onChange={(e) => { const u = [...promoBanners]; u[idx] = { ...u[idx], badge: e.target.value }; setPromoBanners(u); }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Bold Title (on image)</Label>
                      <Input
                        placeholder="e.g. SAFETY GEAR"
                        value={banner.title || ''}
                        onChange={(e) => { const u = [...promoBanners]; u[idx] = { ...u[idx], title: e.target.value }; setPromoBanners(u); }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Card Label (2 lines, use \n)</Label>
                      <Input
                        placeholder="Safety\nEquipment"
                        value={banner.label || ''}
                        onChange={(e) => { const u = [...promoBanners]; u[idx] = { ...u[idx], label: e.target.value }; setPromoBanners(u); }}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Category Name (for link)</Label>
                      <Input
                        placeholder="e.g. Safety"
                        value={banner.category_name || ''}
                        onChange={(e) => { const u = [...promoBanners]; u[idx] = { ...u[idx], category_name: e.target.value }; setPromoBanners(u); }}
                      />
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex lg:flex-col gap-1 w-full lg:w-auto mt-2 lg:mt-0 justify-end">
                    {idx > 0 && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { const u = [...promoBanners]; [u[idx], u[idx-1]] = [u[idx-1], u[idx]]; setPromoBanners(u); }} title="Move up"><ChevronUp className="h-4 w-4" /></Button>}
                    {idx < promoBanners.length - 1 && <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { const u = [...promoBanners]; [u[idx], u[idx+1]] = [u[idx+1], u[idx]]; setPromoBanners(u); }} title="Move down"><ChevronDown className="h-4 w-4" /></Button>}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => setPromoBanners(promoBanners.filter((_, i) => i !== idx))} title="Remove"><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              className="border-dashed"
              onClick={() => setPromoBanners([...promoBanners, { image: '', badge: '', title: '', label: '', category_name: '' }])}
            >
              <Plus className="h-4 w-4 mr-2" />Add Banner
            </Button>
            <Button
              type="button"
              disabled={savingPromo}
              className="bg-[#0c1b44] text-white hover:bg-[#1a2d64]"
              onClick={async () => {
                setSavingPromo(true);
                try {
                  await axios.put(`${API}/promo-banners`, { banners: promoBanners }, { headers: getAuthHeaders() });
                  toast.success('Promo banners saved!');
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
                setSavingPromo(false);
              }}
            >
              {savingPromo ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save Promo Banners
            </Button>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};
export default AdminOffers;
